import * as yup from "yup";
import { Ref, ref, computed } from "vue";
import { FormModes } from "@/models/form";
import { Address, AddressFormInputs } from "@/models/application";
import { useForm } from "vee-validate";
import useValidation from "@/use/validation";
import { $api } from "../../services/api";
import { Response } from "@/models/api";
import { FieldSchema, Option } from "@/models/form";
import { AddressTypeIds } from "@/config/constants";

interface ValidationData {
  formData: Ref<AddressFormInputs>;
  disabled: Ref<boolean>;
  previousAddresses?: Ref<Address[] | undefined>;
  isMounted: Ref<boolean>;
}

export const addressFormSchema = yup.object({
  address1: yup
    .string()
    .label("Permanent Street Address")
    .transform((curr, orig) => (orig === "" ? null : curr))
    .required()
    .test(
      "valid-input",
      "Permanent Street Address can only contain letters, numbers, apostrophes, hyphens, and spaces",
      function (value, testContext) {
        // Only allow standard A-Z a-z 0-9 values, single space, and - '
        const specialCharacterCheck = /[^A-Za-z0-9-' ]/.test(value);
        return specialCharacterCheck === false;
      },
    ),
  address2: yup
    .string()
    .label("Unit Number")
    .notRequired()
    .transform((curr, orig) => (orig === "" ? null : curr))
    .test("valid-unit-number", "Unit Number is a required field", function (value, testContext) {
      const unitType = testContext.parent.addressUnitTypeId;
      const hasInput = value !== undefined && value !== null && value?.trim().length > 0;

      if (!unitType && !hasInput) return true;

      if (
        hasInput ||
        (unitType !== undefined &&
          unitType !== null &&
          !isNaN(Number(unitType)) &&
          Number(unitType) > 0)
      ) {
        if (!hasInput) {
          return testContext.createError({ message: "Unit Number is a required field" });
        }
        // Only allow standard A-Z a-z 0-9 values and single space
        const specialCharacterCheck = /[^A-Za-z0-9\s]/.test(value ?? "");

        if (specialCharacterCheck) {
          return testContext.createError({
            message: "Unit Number can only contain letters, numbers, and spaces",
          });
        }

        return true;
      }

      return false;
    }),
  addressUnitTypeId: yup
    .number()
    .label("Unit Type")
    .notRequired()
    .transform(value => (isNaN(value) ? undefined : value))
    .test("", "Unit Type is a required field", function (value, testContext) {
      const address2 = testContext.parent.address2;

      if (!address2) return true;

      if (
        address2 !== undefined &&
        address2 !== null &&
        (address2.toString() || "")?.trim()?.length > 0
      ) {
        return !isNaN(Number(value)) && Number(value) > 0;
      }

      return false;
    }),
  addressUnitTypeName: yup.string().notRequired(),
  city: yup
    .string()
    .label("City")
    .required()
    .max(40)
    .transform((curr, orig) => (orig === "" ? null : curr))
    .test(
      "valid-input",
      `City can only contain letters, apostrophes, hyphens, and spaces.`,
      function (value) {
        // Only allow standard A-Z a-z values, apostrophes, single space, and - '
        const invalidCharCheck = /[^A-Za-z-' ]/.test(value);
        return invalidCharCheck === false;
      },
    ),
  stateId: yup
    .number()
    .label("State")
    .transform(value => (isNaN(value) ? undefined : value))
    .required()
    .min(1),
  stateName: yup.string().notRequired(),
  postalCode: yup
    .string()
    .label("Zip")
    .required("Zip is a required field")
    .test(
      "is-valid-zip",
      "Please enter a valid ZIP code in 5-digit or 9-digit format (e.g., 12345, 12345-6789, or 123456789)",
      value => {
        // Handles 12345, 12345-6789
        const formatCheck1 = /^[0-9]{5}(?:-[0-9]{4})?$/.test(value);
        // Handles 123456789
        const formatCheck2 = /^[0-9]{9}?$/.test(value);

        if (value === undefined) {
          return false;
        } else if (value.length === 5 && formatCheck1) {
          return true;
        } else if (value.length === 10 && formatCheck1) {
          return true;
        } else if (value.length === 9 && formatCheck2) {
          return true;
        } else {
          return false;
        }
      },
    ),
  countryId: yup
    .number()
    .label("Country")
    .transform(value => (isNaN(value) ? undefined : value))
    .required()
    .min(1),
  countryName: yup.string().notRequired(),
});

export function formatAddress(address: AddressFormInputs, addressUnits: any[]) {
  const unitType = addressUnits?.find((a: any) => a.id === Number(address.addressUnitTypeId));

  address.addressUnitTypeName = unitType ? unitType.abbreviation : null;

  return (
    [
      address.address1,
      address.addressUnitTypeName,
      address.address2,
      address.city,
      address.stateName,
      address.postalCode,
      address.countryName,
    ]
      .filter(n => n)
      .join(", ") || "Not Provided"
  );
}

export default function useAddressForm(data: ValidationData, store: any) {
  const addingAddress = ref<boolean>(false);
  const error = ref<boolean>(false);
  const mode = ref<keyof typeof FormModes>(FormModes.view);
  const showAddressDetails = ref<boolean>(false);
  const savingChanges = ref<boolean>(false);
  const validating = ref<boolean>(false);

  const { validateForm } = useValidation();

  // Dropdown Values
  const countries = computed(() => store.getters["lookupsModule/countryOptions"]);
  const states = computed(() => store.getters["lookupsModule/statesOptions"]);
  const statesData = computed(() => store.state.lookupsModule?.lookups?.states);
  const unitTypes = computed(() => store.getters["lookupsModule/addressUnitsOptions"]);

  const formattedAddress = computed<string>(() =>
    formatAddress(
      data.formData.value as AddressFormInputs,
      store.state.lookupsModule?.lookups?.addressUnits,
    ),
  );

  const showFields = computed<boolean>(() => {
    return (
      mode.value === FormModes.edit ||
      mode.value === FormModes.add ||
      (mode.value === FormModes.view && showAddressDetails.value)
    );
  });

  const validationSchema = computed(() => {
    if (mode.value === FormModes.view || !data.isMounted.value) {
      return yup.object({});
    }

    return addressFormSchema;
  });

  const { values, errors, setValues, validate, resetForm, setFieldValue } = useForm({
    validationSchema,
  });

  const addressSchema = ref({
    address1: {
      name: "address1",
      type: "text",
      subtype: "address",
      label: "Permanent Street Address",
      placeholder: "Street",
      required: true,
      maxLength: 50,
      validate: (schema: FieldSchema) => {
        schema.valid = !errors.value.address1;
        schema.errors = errors.value.address1 ? [errors.value.address1] : [];

        if (!schema.valid) {
          schema.touched = true;
        } else {
          resetAddressField(schema);
        }
      },
    } as FieldSchema,
    address2: {
      name: "address2",
      type: "text",
      subtype: "address",
      label: "Unit Number",
      placeholder: "Unit #",
      required: false,
      maxLength: 25,
      validate: (schema: FieldSchema) => {
        schema.valid = !errors.value.address2;
        schema.errors = errors.value.address2 ? [errors.value.address2] : [];

        if (!schema.valid) {
          schema.touched = true;
        } else {
          resetAddressField(schema);
        }
      },
    } as FieldSchema,
    addressUnitTypeId: {
      name: "addressUnitTypeId",
      type: "select",
      subtype: "address",
      label: "Unit Type",
      required: false,
      validate: (schema: FieldSchema) => {
        schema.valid = !errors.value.addressUnitTypeId;
        schema.errors = errors.value.addressUnitTypeId ? [errors.value.addressUnitTypeId] : [];

        if (!schema.valid) {
          schema.touched = true;
        } else {
          resetAddressField(schema);
        }
      },
    } as FieldSchema,
    city: {
      name: "city",
      type: "text",
      label: "City",
      subtype: "address",
      placeholder: "City",
      required: true,
      maxLength: 40,
      validate: (schema: FieldSchema) => {
        schema.valid = !errors.value.city;
        schema.errors = errors.value.city ? [errors.value.city] : [];

        if (!schema.valid) {
          schema.touched = true;
        } else {
          resetAddressField(schema);
        }
      },
    } as FieldSchema,
    stateId: {
      name: "stateId",
      type: "select",
      subtype: "address",
      label: "State",
      required: true,
      validate: (schema: FieldSchema) => {
        schema.valid = !errors.value.stateId;
        schema.errors = errors.value.stateId ? [errors.value.stateId] : [];

        if (!schema.valid) {
          schema.touched = true;
        } else {
          resetAddressField(schema);
        }
      },
    } as FieldSchema,
    postalCode: {
			name: "postalCode",
      type: "zipcode",
      label: "Zip",
      placeholder: "Zip",
      errorMessage: "Zip code is required.",
      required: true,
      validate: (schema: FieldSchema) => {
        schema.valid = !errors.value.postalCode;
        schema.errors = errors.value.postalCode ? [errors.value.postalCode] : [];

        if (!schema.valid) {
          schema.touched = true;
        } else {
          resetAddressField(schema);
        }
      },
    } as FieldSchema,
    countryId: {
      name: "countryId",
      type: "select",
      label: "Country",
      required: true,
      validate: (schema: FieldSchema) => {
        schema.valid = !errors.value.countryId;
        schema.errors = errors.value.countryId ? [errors.value.countryId] : [];

        if (!schema.valid) {
          schema.touched = true;
        } else {
          resetAddressField(schema);
        }
      },
    } as FieldSchema,
  });

  function getOption(value: number | string, options: Option[]) {
    const id = !isNaN(Number(value)) ? Number(value) : value;
    const option = options.find((o: any) => o.value === id);

    return option;
  }

  function resetAddressField(schema: any) {
    schema.errors = [];
    schema.touched = false;
    schema.valid = undefined;
  }

  function resetAddressSchema() {
    Object.entries(addressSchema.value).forEach(([key, value]) => {
      value.touched = false;
      value.valid = undefined;
      value.errors = [];
    });
  }

  function isInvalid(schema: FieldSchema) {
    return !schema.valid;
  }

  async function onValidate() {
    const validationResponse = await validate();

    validateForm(addressSchema.value, data.formData);

    return validationResponse;
  }

  async function saveAddressAsync() {
    const errorResponse = {
      success: false,
      errors: [],
      results: null,
    } as Response<AddressFormInputs>;

    if (data.formData.value?.addressId <= 0) {
      const response = await $api.applicant.addAddress(
        data.formData.value.applicantId || 0,
        data.formData.value,
      );

      if (response?.errors) {
        savingChanges.value = false;
        return errorResponse;
      }

      await store.dispatch("updateApplicantAddresses", {
        applicantId: data.formData.value.applicantId,
        addresses: response,
      });

      let updatedAddress = response
        .filter((a: any) => a.addressTypeId === AddressTypeIds.PreviousAddress)
        .pop();

      console.log("updated address", updatedAddress);

      if (updatedAddress.id && updatedAddress.id > 0) {
        updatedAddress = {
          ...updatedAddress,
          stipulationId: data.formData.value.stipulationId,
          addressId: updatedAddress.id,
          mode: FormModes.view,
        };

        return {
          success: true,
          errors: [],
          results: updatedAddress,
        };
      } else {
        return errorResponse;
      }
    }

    if (data.formData.value.addressId > 0) {
      const response = await $api.applicant.updateAddress(
        data.formData.value.applicantId || 0,
        data.formData.value,
      );

      if (response.id && response.id > 0) {
        const updatedAddress = {
          ...response,
          stipulationId: data.formData.value.stipulationId,
          addressId: response.id,
          mode: FormModes.view,
        };

        return {
          success: true,
          errors: [],
          results: updatedAddress,
        };
      } else {
        return errorResponse;
      }
    }
  }

  return {
    addingAddress,
    addressFormSchema,
    addressSchema,
    countries,
    error,
    errors,
    formattedAddress,
    getOption,
    isInvalid,
    mode,
    onValidate,
    resetForm,
    resetAddressSchema,
    saveAddressAsync,
    savingChanges,
    setValues,
    setFieldValue,
    showAddressDetails,
    showFields,
    states,
    statesData,
    unitTypes,
    validate,
    validateForm,
    validating,
    validationSchema,
    values,
  };
}
